import { conversationHasExactContacts } from '../../../utils/messaging/conversation';
import { filterSystemAndCurrentUser } from '../../../utils/messaging/user';
import { service } from '@ember/service';
import { setOwner } from '@ember/application';
import { task, waitForProperty } from 'ember-concurrency';

export default class WebsocketActivityHandlersConversation {
  @service messaging;
  @service messagingConversation;
  @service store;

  constructor(owner) {
    setOwner(this, owner);
  }

  // To prevent the addition of duplicates when a user creates a conversation themself, we need to
  // wait for any pending conversation creation to finish (and be assigned an id) before handling
  // the creation activity (which compares ids to prevent duplicates).
  waitForCreateConversationToComplete = task(async () => {
    return await waitForProperty(
      this.messagingConversation,
      'saveNewConversation.isRunning',
      false
    );
  });

  handleActivity(response) {
    const statusHandlers = {
      updated: this.#handleUpdatedActivity,
      read: this.#handleReadActivity,
      created: this.#handleCreationActivity,
    };
    const handler = response.status ? statusHandlers[response.status] : statusHandlers.updated;
    handler.call(this, response.payload);
  }

  #handleUpdatedActivity(payload) {
    const conversation = this.store.normalize('messaging-conversation', payload.data).data;
    const record = this.store.peekRecord('messaging-conversation', conversation.id);

    if (!record) return;
    record.setProperties(conversation.attributes);

    if (record.inactive) return;
    this.messagingConversation.inactiveReasons = [];
  }

  #handleReadActivity(payload) {
    const conversation = this.store.normalize('messaging-conversation', payload.data).data;
    this.messaging.currentProfile?.removeUnreadConversation(conversation.id);
  }

  async #handleCreationActivity(payload) {
    await this.waitForCreateConversationToComplete.perform();

    const conversationId = payload.data.id;
    const persistedConversation = this.#ensureConversationInStore(conversationId, payload);
    const conversations = this.messaging.conversations;

    if (this.#conversationExists(conversations, conversationId)) return;

    this.#addOrReplaceConversationRecord(conversations, persistedConversation);
  }

  #addOrReplaceConversationRecord(conversations, persistedConversation) {
    const persistedConversationUsers = filterSystemAndCurrentUser(
      persistedConversation.users,
      this.messaging.currentProfile?.user
    );
    const matchingUnsavedConversation = conversations.content.find(
      conv => conv.isNew && conversationHasExactContacts(conv, persistedConversationUsers)
    );

    if (matchingUnsavedConversation) {
      persistedConversation.messageDraft = matchingUnsavedConversation.messageDraft;

      const index = conversations.content.indexOf(matchingUnsavedConversation);
      conversations.replace(index, 1, [persistedConversation]);

      if (matchingUnsavedConversation === this.messagingConversation.selectedConversation) {
        this.messagingConversation.openConversationPage(persistedConversation);
      }
    } else {
      conversations.unshiftObjects([persistedConversation]);
    }
  }

  #conversationExists(conversations, conversationId) {
    return conversations.content.some(conv => conv.id === conversationId);
  }

  #ensureConversationInStore(conversationId, payload) {
    this.store.pushPayload('messaging-conversation', payload);
    return this.store.peekRecord('messaging-conversation', conversationId);
  }
}
